import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Plus, Trash2, ChevronDown, ArrowLeft, Calculator, IndianRupee, Package, Percent, PieChart, FlaskConical, Tag } from 'lucide-react';

const STORAGE_KEY = 'nistala_formulation_items';

const loadItems = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
  catch { return []; }
};
const saveItems = (items) => localStorage.setItem(STORAGE_KEY, JSON.stringify(items));

const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MARGIN_OPTIONS = [10, 20, 25, 30, 40];

const FormulationCalculator = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');

  useEffect(() => { if (!token) navigate('/admin'); }, [token, navigate]);

  const [items, setItems] = useState(loadItems);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showNewItem, setShowNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newIngredients, setNewIngredients] = useState([{ name: '' }]);

  const [ingredientData, setIngredientData] = useState([]);
  const [labelCost, setLabelCost] = useState('');
  const [packagingCost, setPackagingCost] = useState('');
  const [containerCost, setContainerCost] = useState('');
  const [deliveryCost, setDeliveryCost] = useState('');
  const [additionalCost, setAdditionalCost] = useState('');
  const [margin, setMargin] = useState(25);
  const [customMargin, setCustomMargin] = useState('');
  const [isCustomMargin, setIsCustomMargin] = useState(false);
  const [batchSize, setBatchSize] = useState('');

  const selectedItem = items.find(d => d.id === selectedItemId);

  useEffect(() => {
    if (selectedItem) {
      setIngredientData(selectedItem.ingredients.map(ing => ({ name: ing.name, qty: '', costPerUnit: '' })));
      setLabelCost(''); setPackagingCost(''); setContainerCost('');
      setDeliveryCost(''); setAdditionalCost('');
      setMargin(25); setCustomMargin(''); setIsCustomMargin(false); setBatchSize('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId]);

  const handleCreateItem = () => {
    if (!newItemName.trim()) return;
    const validIngs = newIngredients.filter(i => i.name.trim());
    if (validIngs.length === 0) return;
    const item = { id: Date.now().toString(), name: newItemName.trim(), ingredients: validIngs };
    const updated = [...items, item];
    setItems(updated); saveItems(updated);
    setSelectedItemId(item.id);
    setShowNewItem(false); setNewItemName(''); setNewIngredients([{ name: '' }]);
  };

  const handleDeleteItem = (id) => {
    if (!window.confirm('Delete this formulation?')) return;
    const updated = items.filter(d => d.id !== id);
    setItems(updated); saveItems(updated);
    if (selectedItemId === id) setSelectedItemId(null);
  };

  const activeMargin = isCustomMargin ? (parseFloat(customMargin) || 0) : margin;

  const calculations = useMemo(() => {
    const ingredientCosts = ingredientData.map(i => (parseFloat(i.qty) || 0) * (parseFloat(i.costPerUnit) || 0));
    const totalIngredient = ingredientCosts.reduce((s, c) => s + c, 0);
    const label = parseFloat(labelCost) || 0;
    const packaging = parseFloat(packagingCost) || 0;
    const container = parseFloat(containerCost) || 0;
    const totalLabelPackaging = label + packaging + container;
    const delivery = parseFloat(deliveryCost) || 0;
    const additional = parseFloat(additionalCost) || 0;
    const totalCost = totalIngredient + totalLabelPackaging + delivery + additional;
    const sellingPrice = totalCost * (1 + activeMargin / 100);
    const profit = sellingPrice - totalCost;
    const batch = parseInt(batchSize) || 0;
    return { ingredientCosts, totalIngredient, label, packaging, container, totalLabelPackaging, delivery, additional, totalCost, sellingPrice, profit, costPerPiece: batch > 0 ? totalCost / batch : 0, pricePerPiece: batch > 0 ? sellingPrice / batch : 0 };
  }, [ingredientData, labelCost, packagingCost, containerCost, deliveryCost, additionalCost, activeMargin, batchSize]);

  const updateIngredient = (idx, field, value) => {
    setIngredientData(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[#FDF8F0] pt-24 pb-12" data-testid="formulation-calculator-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        <div className="flex items-center gap-3 mb-6">
          <Button onClick={() => navigate('/admin')} variant="ghost" className="text-[#7a6555] hover:text-[#2C1810] hover:bg-[#E8DFD3]/50 cursor-pointer p-2" data-testid="back-to-admin-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Formulation Cost Calculator
            </h1>
            <p className="text-sm text-[#7a6555]">Calculate formulation costs, margins & selling prices</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <div className="lg:col-span-2 space-y-5">

            {/* Section 1: Formulation Selection */}
            <Card className="border border-[#E8DFD3] shadow-sm bg-white" data-testid="item-selection-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FlaskConical size={18} className="text-[#C4844A]" />
                  <h2 className="text-base font-bold text-[#2C1810]">Select Formulation</h2>
                </div>

                {!showNewItem ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <select
                        value={selectedItemId || ''}
                        onChange={e => setSelectedItemId(e.target.value || null)}
                        className="w-full border border-[#E8DFD3] rounded-lg px-4 py-2.5 text-sm bg-white appearance-none pr-10 text-[#2C1810] focus:outline-none focus:ring-2 focus:ring-[#D4A574]/40 focus:border-[#D4A574]"
                        data-testid="item-select"
                      >
                        <option value="">-- Choose a formulation --</option>
                        {items.map(d => <option key={d.id} value={d.id}>{d.name} ({d.ingredients.length} ingredients)</option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a6555] pointer-events-none" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => setShowNewItem(true)} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white text-sm cursor-pointer" data-testid="create-item-btn">
                        <Plus size={15} className="mr-1.5" /> New Formulation
                      </Button>
                      {selectedItemId && (
                        <Button onClick={() => handleDeleteItem(selectedItemId)} variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 text-sm cursor-pointer" data-testid="delete-item-btn">
                          <Trash2 size={15} className="mr-1.5" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 border border-[#D4A574]/30 bg-[#FDF8F0] rounded-xl p-4">
                    <Input
                      placeholder="Formulation name (e.g., Face Cream, Hair Oil)"
                      value={newItemName}
                      onChange={e => setNewItemName(e.target.value)}
                      className="border-[#E8DFD3]"
                      data-testid="new-item-name-input"
                    />
                    <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">Ingredients</p>
                    {newIngredients.map((ing, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          placeholder={`Ingredient ${idx + 1} (e.g., Base Oil, Fragrance)`}
                          value={ing.name}
                          onChange={e => setNewIngredients(prev => prev.map((item, i) => i === idx ? { name: e.target.value } : item))}
                          className="border-[#E8DFD3] flex-1"
                          data-testid={`new-ingredient-${idx}`}
                        />
                        {newIngredients.length > 1 && (
                          <Button onClick={() => setNewIngredients(prev => prev.filter((_, i) => i !== idx))} variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50 cursor-pointer px-2">
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button onClick={() => setNewIngredients(prev => [...prev, { name: '' }])} variant="outline" size="sm" className="text-[#5C7A3A] border-[#5C7A3A]/30 hover:bg-[#5C7A3A]/5 cursor-pointer text-xs" data-testid="add-ingredient-btn">
                      <Plus size={13} className="mr-1" /> Add Ingredient
                    </Button>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={handleCreateItem} className="bg-[#C4844A] hover:bg-[#A96E3A] text-white text-sm cursor-pointer" data-testid="save-item-btn">
                        Save Formulation
                      </Button>
                      <Button onClick={() => { setShowNewItem(false); setNewItemName(''); setNewIngredients([{ name: '' }]); }} variant="ghost" className="text-[#7a6555] text-sm cursor-pointer">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 2: Ingredients Table */}
            {selectedItem && (
              <Card className="border border-[#E8DFD3] shadow-sm bg-white" data-testid="ingredients-table-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Package size={18} className="text-[#5C7A3A]" />
                    <h2 className="text-base font-bold text-[#2C1810]">{selectedItem.name} - Ingredients</h2>
                  </div>

                  <div className="hidden sm:grid grid-cols-[1fr_120px_120px_100px] gap-3 mb-2 px-1">
                    <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">Ingredient</p>
                    <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">Qty (g/ml)</p>
                    <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">Cost/unit</p>
                    <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide text-right">Total</p>
                  </div>

                  <div className="space-y-2">
                    {ingredientData.map((ing, idx) => (
                      <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_100px] gap-2 sm:gap-3 bg-[#FDF8F0] rounded-lg p-3 sm:p-2 sm:items-center" data-testid={`ingredient-row-${idx}`}>
                        <p className="text-sm font-medium text-[#2C1810]">{ing.name}</p>
                        <div>
                          <span className="sm:hidden text-xs text-[#7a6555]">Qty (g/ml)</span>
                          <Input type="number" min="0" step="any" placeholder="0" value={ing.qty} onChange={e => updateIngredient(idx, 'qty', e.target.value)} className="border-[#E8DFD3] text-sm h-9" data-testid={`ingredient-qty-${idx}`} />
                        </div>
                        <div>
                          <span className="sm:hidden text-xs text-[#7a6555]">Cost/unit (₹)</span>
                          <Input type="number" min="0" step="any" placeholder="0.00" value={ing.costPerUnit} onChange={e => updateIngredient(idx, 'costPerUnit', e.target.value)} className="border-[#E8DFD3] text-sm h-9" data-testid={`ingredient-cost-${idx}`} />
                        </div>
                        <p className="text-sm font-semibold text-[#C4844A] text-right" data-testid={`ingredient-total-${idx}`}>₹{fmt(calculations.ingredientCosts[idx])}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end mt-3 pt-3 border-t border-[#E8DFD3]">
                    <div className="text-right">
                      <p className="text-xs text-[#7a6555]">Total Ingredient Cost</p>
                      <p className="text-lg font-bold text-[#2C1810]" data-testid="total-ingredient-cost">₹{fmt(calculations.totalIngredient)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 3: Label / Packaging Costs */}
            {selectedItem && (
              <Card className="border border-[#E8DFD3] shadow-sm bg-white" data-testid="label-packaging-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Tag size={18} className="text-[#C4844A]" />
                    <h2 className="text-base font-bold text-[#2C1810]">Label / Packaging</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-medium text-[#7a6555] mb-1 block">Label Cost (₹)</label>
                      <Input type="number" min="0" step="any" placeholder="0" value={labelCost} onChange={e => setLabelCost(e.target.value)} className="border-[#E8DFD3]" data-testid="label-cost-input" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#7a6555] mb-1 block">Packaging Cost (₹)</label>
                      <Input type="number" min="0" step="any" placeholder="0" value={packagingCost} onChange={e => setPackagingCost(e.target.value)} className="border-[#E8DFD3]" data-testid="packaging-cost-input" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#7a6555] mb-1 block">Container Cost (₹)</label>
                      <Input type="number" min="0" step="any" placeholder="0" value={containerCost} onChange={e => setContainerCost(e.target.value)} className="border-[#E8DFD3]" data-testid="container-cost-input" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 4: Additional Costs */}
            {selectedItem && (
              <Card className="border border-[#E8DFD3] shadow-sm bg-white" data-testid="additional-costs-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <IndianRupee size={18} className="text-[#C4844A]" />
                    <h2 className="text-base font-bold text-[#2C1810]">Additional Costs</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-[#7a6555] mb-1 block">Delivery Cost (₹)</label>
                      <Input type="number" min="0" step="any" placeholder="0" value={deliveryCost} onChange={e => setDeliveryCost(e.target.value)} className="border-[#E8DFD3]" data-testid="delivery-cost-input" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#7a6555] mb-1 block">Additional Cost (₹)</label>
                      <Input type="number" min="0" step="any" placeholder="0" value={additionalCost} onChange={e => setAdditionalCost(e.target.value)} className="border-[#E8DFD3]" data-testid="additional-cost-input" />
                    </div>
                  </div>
                  <p className="text-xs text-[#7a6555]/70 mt-2">Gas, electricity, labor, platform fees, etc.</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* RIGHT: Summary Panel */}
          {selectedItem && (
            <div className="space-y-5">
              <Card className="border border-[#E8DFD3] shadow-md bg-white sticky top-24" data-testid="summary-card">
                <CardContent className="p-5 space-y-5">
                  <div className="flex items-center gap-2">
                    <Calculator size={18} className="text-[#5C7A3A]" />
                    <h2 className="text-base font-bold text-[#2C1810]">Summary</h2>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#7a6555]">Ingredients</span>
                      <span className="font-medium text-[#2C1810]">₹{fmt(calculations.totalIngredient)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7a6555]">Label</span>
                      <span className="font-medium text-[#2C1810]">₹{fmt(calculations.label)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7a6555]">Packaging</span>
                      <span className="font-medium text-[#2C1810]">₹{fmt(calculations.packaging)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7a6555]">Container</span>
                      <span className="font-medium text-[#2C1810]">₹{fmt(calculations.container)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7a6555]">Delivery</span>
                      <span className="font-medium text-[#2C1810]">₹{fmt(calculations.delivery)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7a6555]">Additional</span>
                      <span className="font-medium text-[#2C1810]">₹{fmt(calculations.additional)}</span>
                    </div>
                    <div className="border-t border-[#E8DFD3] pt-2 flex justify-between">
                      <span className="font-bold text-[#2C1810]">Total Cost</span>
                      <span className="font-bold text-[#2C1810] text-base" data-testid="total-cost">₹{fmt(calculations.totalCost)}</span>
                    </div>
                  </div>

                  {/* Margin selector */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Percent size={14} className="text-[#C4844A]" />
                      <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">Profit Margin</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {MARGIN_OPTIONS.map(m => (
                        <button
                          key={m}
                          onClick={() => { setMargin(m); setIsCustomMargin(false); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                            !isCustomMargin && margin === m ? 'bg-[#5C7A3A] text-white shadow-sm' : 'bg-[#FDF8F0] text-[#7a6555] hover:bg-[#E8DFD3]'
                          }`}
                          data-testid={`margin-${m}`}
                        >
                          {m}%
                        </button>
                      ))}
                      <button
                        onClick={() => setIsCustomMargin(true)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                          isCustomMargin ? 'bg-[#5C7A3A] text-white shadow-sm' : 'bg-[#FDF8F0] text-[#7a6555] hover:bg-[#E8DFD3]'
                        }`}
                        data-testid="margin-custom"
                      >
                        Custom
                      </button>
                    </div>
                    {isCustomMargin && (
                      <div className="flex items-center gap-2">
                        <Input type="number" min="0" step="any" placeholder="Enter %" value={customMargin} onChange={e => setCustomMargin(e.target.value)} className="border-[#E8DFD3] text-sm h-9 w-24" data-testid="custom-margin-input" />
                        <span className="text-sm text-[#7a6555]">%</span>
                      </div>
                    )}
                  </div>

                  {/* Selling Price */}
                  <div className="bg-gradient-to-br from-[#5C7A3A] to-[#4a6530] rounded-xl p-4 text-white">
                    <p className="text-xs font-medium text-white/70 mb-1">Selling Price ({activeMargin}% margin)</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="selling-price">₹{fmt(calculations.sellingPrice)}</p>
                    <div className="flex justify-between mt-2 pt-2 border-t border-white/15 text-xs">
                      <span className="text-white/70">Profit</span>
                      <span className="font-semibold" data-testid="profit-amount">₹{fmt(calculations.profit)}</span>
                    </div>
                  </div>

                  {/* Batch Size */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart size={14} className="text-[#C4844A]" />
                      <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">Batch Size (Optional)</p>
                    </div>
                    <Input type="number" min="1" step="1" placeholder="e.g., 50 units" value={batchSize} onChange={e => setBatchSize(e.target.value)} className="border-[#E8DFD3] text-sm" data-testid="batch-size-input" />
                    {parseInt(batchSize) > 0 && (
                      <div className="mt-3 bg-[#FDF8F0] rounded-lg p-3 space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#7a6555]">Cost / unit</span>
                          <span className="font-semibold text-[#2C1810]" data-testid="cost-per-piece">₹{fmt(calculations.costPerPiece)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#7a6555]">Sell / unit</span>
                          <span className="font-bold text-[#5C7A3A]" data-testid="price-per-piece">₹{fmt(calculations.pricePerPiece)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!selectedItem && !showNewItem && (
            <div className="lg:col-span-1 flex items-start justify-center pt-8">
              <div className="text-center text-[#7a6555]">
                <FlaskConical size={40} className="mx-auto mb-3 text-[#D4A574]/50" />
                <p className="text-sm">Select or create a formulation to start calculating</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormulationCalculator;
